import { useReducer } from 'react';

export type UseStepper = [UseStepperState, UseStepperDispatch];

export interface UseStepperDispatch {
  onStepComplete: (step: number) => void;
  onStepIncomplete: (step: number) => void;
  completeActiveStepAndSetNextStep: (step: number) => void;
  incompleteActiveStepAndSetStep: (step: number) => void;
  setActiveStep: (step: number) => void;
}

export interface UseStepperState {
  completedSteps: number[];
  activeStep: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isStepComplete: (step: number) => boolean;
}
type UseStepperInternalState = {
  completedSteps: Set<number>;
  activeStep: number;
  isFirstStep: boolean;
  isLastStep: boolean;
};
type Action =
  | { type: 'STEP_COMPLETE'; payload: number }
  | { type: 'STEP_INCOMPLETE'; payload: number }
  | { type: 'SET_ACTIVE_STEP_COMPLETE_AND_SET_STEP'; payload: number }
  | { type: 'SET_ACTIVE_STEP_INCOMPLETE_AND_SET_STEP'; payload: number }
  | { type: 'SET_ACTIVE_STEP'; payload: number };

const getDefaultState = (): UseStepperInternalState => ({
  completedSteps: new Set([]),
  activeStep: 1,
  isFirstStep: true,
  isLastStep: false
});

const stepperReducer = (
  state: UseStepperInternalState,
  action: Action
): UseStepperInternalState => {
  switch (action.type) {
    case 'STEP_COMPLETE':
      return { ...state, completedSteps: state.completedSteps.add(action.payload) };
    case 'STEP_INCOMPLETE':
      state.completedSteps.delete(action.payload);
      return { ...state, completedSteps: state.completedSteps };
    case 'SET_ACTIVE_STEP_COMPLETE_AND_SET_STEP':
      return {
        ...state,
        activeStep: action.payload,
        completedSteps: state.completedSteps.add(state.activeStep)
      };
    case 'SET_ACTIVE_STEP_INCOMPLETE_AND_SET_STEP': {
      state.completedSteps.delete(action.payload);

      return {
        ...state,
        activeStep: action.payload,
        completedSteps: state.completedSteps
      };
    }
    case 'SET_ACTIVE_STEP':
      return { ...state, activeStep: action.payload };
    default:
      return getDefaultState();
  }
};

const useStepper = (numberOfSteps: number): UseStepper => {
  const [state, dispatch] = useReducer<React.Reducer<UseStepperInternalState, Action>>(
    stepperReducer,
    getDefaultState()
  );

  const onStepComplete = (step: number): void => dispatch({ type: 'STEP_COMPLETE', payload: step });

  const onStepIncomplete = (step: number): void =>
    dispatch({ type: 'STEP_INCOMPLETE', payload: step });

  const completeActiveStepAndSetStep = (step: number): void =>
    dispatch({
      type: 'SET_ACTIVE_STEP_COMPLETE_AND_SET_STEP',
      payload: step
    });

  const incompleteActiveStepAndSetStep = (step: number): void =>
    dispatch({
      type: 'SET_ACTIVE_STEP_COMPLETE_AND_SET_STEP',
      payload: step
    });

  const setActiveStep = (step: number): void =>
    dispatch({ type: 'SET_ACTIVE_STEP', payload: step });

  const isStepComplete = (step: number) =>
    Array.from(state.completedSteps ?? []).some(x => x === step);

  return [
    {
      ...state,
      isFirstStep: state.activeStep === 1,
      isLastStep: state.activeStep === numberOfSteps,
      isStepComplete,
      completedSteps: Array.from(state.completedSteps)
    },
    {
      onStepComplete,
      onStepIncomplete,
      completeActiveStepAndSetNextStep: completeActiveStepAndSetStep,
      incompleteActiveStepAndSetStep,
      setActiveStep
    }
  ];
};

export default useStepper;
